// Updated AgentContext.jsx - Synchronized with UnifiedAuthContext
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './UnifiedAuthContext'; // Import the unified auth context
import agentApiService from '../services/AgentApiService';

const AgentContext = createContext();

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};

export const AgentProvider = ({ children }) => {
  // Get user data from UnifiedAuthContext
  const { user: unifiedUser, login: unifiedLogin, register: unifiedRegister, logout: unifiedLogout, updateUser: unifiedUpdateUser, loading: unifiedLoading } = useAuth();
  
  // Core agent state (now synced with unified context)
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(false); // Use unified loading for auth, this for agent-specific operations
  const [error, setError] = useState(null);
  
  // Agent-specific data
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    featured: 0
  });
  
  // UI state
  const [currentView, setCurrentView] = useState('dashboard');

  // Sync agent state with unified user state
  useEffect(() => {
    console.log('Unified user changed:', unifiedUser);
    console.log('Unified loading:', unifiedLoading);
    
    if (unifiedUser) {
      // User is authenticated in unified context
      setAgent(unifiedUser);
      
      // Ensure agentApiService has the correct token
      const token = localStorage.getItem('access_token');
      if (token && agentApiService.setToken) {
        agentApiService.setToken(token);
      }
      
      // If user is an agent, load their agent-specific data
      if (unifiedUser.isAgent) {
        console.log('User is an agent, loading listings...');
        loadAgentListings();
      } else {
        // Clear agent-specific data if user is not an agent
        console.log('User is not an agent, clearing agent data...');
        setListings([]);
        setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
      }
    } else if (!unifiedLoading) {
      // Only clear data if unified context has finished loading and truly has no user
      console.log('Unified context finished loading with no user, clearing all data...');
      setAgent(null);
      setListings([]);
      setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
      
      // Clear agent API service token
      if (agentApiService.clearToken) {
        agentApiService.clearToken();
      }
    }
    // If unifiedLoading is true, don't clear anything yet - wait for it to finish
  }, [unifiedUser, unifiedLoading]);

  // Initialize agent data on mount (only if not already handled by unified context)
  useEffect(() => {
    // Only run initialization if:
    // 1. Unified context is done loading
    // 2. No unified user found
    // 3. But there's still a token in localStorage (meaning unified context failed to load user)
    if (!unifiedLoading && !unifiedUser && localStorage.getItem('access_token')) {
      console.log('Unified context finished loading but no user found, trying agent initialization...');
      initializeAgent();
    }
  }, [unifiedLoading, unifiedUser]);

  const initializeAgent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated (has token)
      if (agentApiService.isAuthenticated()) {
        console.log('Token found, attempting to load profile');
        await loadAgentProfile();
      } else {
        console.log('No token found, user needs to login');
        // Not authenticated, just set loading to false
        setAgent(null);
        setListings([]);
        setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
      }
    } catch (error) {
      console.error('Failed to initialize agent:', error);
      // For authentication errors, clear the token and don't show error
      if (error.message.includes('Authentication failed') || error.message.includes('401')) {
        agentApiService.clearToken();
        setAgent(null);
        setListings([]);
        setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAgentProfile = async () => {
    try {
      console.log('Attempting to load agent profile...');
      const profile = await agentApiService.getCurrentProfile();
      console.log('Profile loaded:', profile);
      
      setAgent(profile);
      
      // Only load listings if user is an agent
      if (profile.isAgent) {
        await loadAgentListings();
      } else {
        // Reset listings and stats if not an agent
        setListings([]);
        setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
      }
    } catch (error) {
      console.error('Failed to load agent profile:', error);
      
      // If profile doesn't exist or user not authenticated, clear state
      if (error.message.includes('not found') || 
          error.message.includes('404') || 
          error.message.includes('Authentication failed')) {
        setAgent(null);
        setListings([]);
        setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
        
        // Clear token if authentication failed
        if (error.message.includes('Authentication failed')) {
          agentApiService.clearToken();
        }
        return;
      }
      
      throw error; // Re-throw other errors
    }
  };

  const loadAgentListings = async () => {
    try {
      console.log('Loading agent listings using new approach...');
      
      const response = await agentApiService.getMyListings();
      console.log('Agent listings response:', response);
      
      // Set listings from response
      const listingsArray = response.listings || [];
      setListings(listingsArray);
      
      // Set stats from API response (already calculated in the service)
      setStats({
        total: response.total_listings || listingsArray.length,
        approved: response.approved_listings || 0,
        pending: response.pending_listings || 0,
        featured: response.featured_listings || 0
      });
      
      console.log('Final listings set:', listingsArray);
      console.log('Final stats set:', {
        total: response.total_listings || listingsArray.length,
        approved: response.approved_listings || 0,
        pending: response.pending_listings || 0,
        featured: response.featured_listings || 0
      });
      
    } catch (error) {
      console.error('Failed to load agent listings:', error);
      setListings([]);
      setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
    }
  };

  // Authentication methods - Now delegate to unified context
  const loginAgent = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use unified context login
      const userData = await unifiedLogin(credentials.email, credentials.password);
      
      if (userData) {
        // Ensure agentApiService uses the same token
        const token = localStorage.getItem('access_token');
        if (token && agentApiService.setToken) {
          agentApiService.setToken(token);
        }
        
        // Agent state will be updated via useEffect when unifiedUser changes
        setCurrentView('dashboard');
        return { user_data: userData };
      }
      
      throw new Error('Invalid login response');
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const registerAgent = async (agentData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Calling agentApiService.registerAgent...');
      
      // ✅ Use the existing AgentApiService.registerAgent method directly
      const response = await agentApiService.registerAgent(agentData);
      
      console.log('Registration successful:', response);
      
      if (response.access_token) {
        // The token is already set by agentApiService.registerAgent
        // Just update localStorage for unified context sync
        localStorage.setItem('access_token', response.access_token);
        
        // Load the new agent profile
        const agentProfile = await agentApiService.getCurrentProfile();
        setAgent(agentProfile);
        
        setCurrentView('dashboard');
        return response;
      }
      
      throw new Error('No access token in response');
      
    } catch (error) {
      console.error('Registration failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logoutAgent = () => {
    // Clear agent API service token first
    if (agentApiService.clearToken) {
      agentApiService.clearToken();
    }
    
    // Use unified context logout
    unifiedLogout();
    
    // Clear agent-specific state
    setListings([]);
    setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
    setCurrentView('dashboard');
    setError(null);
  };

  // Agent conversion methods
  const convertToAgent = async (agentData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await agentApiService.convertUserToAgent(agentData);
      
      // Update both contexts
      await loadAgentProfile();
      
      // Refresh unified context user data
      if (unifiedUser) {
        const token = localStorage.getItem('access_token');
        if (token) {
          try {
            const updatedUserData = await fetch('https://app.akiya2.com/get-user-data', {
              headers: { Authorization: `Bearer ${token}` },
            });
            const userData = await updatedUserData.json();
            // This will trigger the useEffect to update agent state
          } catch (err) {
            console.error('Failed to refresh unified user data:', err);
          }
        }
      }
      
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const restoreAgentStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await agentApiService.restoreAgentStatus();
      await loadAgentProfile();
      
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const convertToUser = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await agentApiService.convertAgentToUser();
      await loadAgentProfile();
      setListings([]);
      setStats({ total: 0, approved: 0, pending: 0, featured: 0 });
      
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const checkAgentConversionData = async () => {
    try {
      return await agentApiService.checkAgentData();
    } catch (error) {
      console.error('Failed to check agent data:', error);
      return { can_restore_agent_data: false };
    }
  };

  // Profile management - Now sync with unified context
  const updateAgentProfile = async (updateData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Update via agent API
      const updatedAgent = await agentApiService.updateAgentProfile(updateData);
      
      // Also update unified context if the update affects general user data
      if (updateData.email || updateData.name || updateData.phone) {
        try {
          await unifiedUpdateUser(updateData);
        } catch (err) {
          console.error('Failed to sync with unified context:', err);
        }
      }
      
      setAgent(updatedAgent.agent_data || updatedAgent);
      
      return updatedAgent;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Listing management
  const addListing = async (listingFormData) => {
    try {
      setError(null);
      
      const response = await agentApiService.addListing(listingFormData);
      
      // Reload listings to get updated data
      await loadAgentListings();
      
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const editListing = async (propertyId, updateData) => {
    try {
      setError(null);
      
      const response = await agentApiService.editListing(propertyId, updateData);
      
      // Reload listings to get updated data instead of manual update
      await loadAgentListings();
      
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const refreshListings = async () => {
    try {
      await loadAgentListings();
    } catch (error) {
      console.error('Failed to refresh listings:', error);
      setError('Failed to refresh listings');
    }
  };

  // Utility methods to get listings by status
  const getListingsByStatus = (status) => {
    return listings.filter(listing => {
      switch (status) {
        case 'approved':
          return listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval');
        case 'pending':
          return listing.status === 'pending_approval' || listing.underCheck;
        case 'featured':
          return listing.featured;
        default:
          return true;
      }
    });
  };

  // Navigation helpers
  const navigateTo = (view) => {
    setCurrentView(view);
  };

  const goToDashboard = () => setCurrentView('dashboard');
  const goToListings = () => setCurrentView('listings');
  const goToProfile = () => setCurrentView('profile');
  const goToAddListing = () => setCurrentView('add-listing');

  // Utility methods - Now use unified context data
  const isAgent = () => (agent?.isAgent === true) || (unifiedUser?.isAgent === true);
  const isSuperAdmin = () => (agent?.isSuperAdmin === true) || (unifiedUser?.isAdmin === true);
  const isAuthenticated = () => !!unifiedUser && !!agent;

  // Error handling
  const clearError = () => setError(null);

  const contextValue = {
    // State - Use unified loading for auth operations
    agent: agent || unifiedUser, // Fallback to unified user if agent is null
    loading: loading || unifiedLoading,
    error,
    listings,
    stats,
    currentView,

    // Authentication - Delegate to unified context
    loginAgent,
    registerAgent,
    logoutAgent,
    isAuthenticated,
    isAgent,
    isSuperAdmin,

    // Agent conversion
    convertToAgent,
    restoreAgentStatus,
    convertToUser,
    checkAgentConversionData,

    // Profile management
    updateAgentProfile,
    loadAgentProfile,

    // Listing management
    addListing,
    editListing,
    refreshListings,
    loadAgentListings,
    getListingsByStatus,

    // Navigation
    navigateTo,
    goToDashboard,
    goToListings,
    goToProfile,
    goToAddListing,

    // Utilities
    clearError
  };

  return (
    <AgentContext.Provider value={contextValue}>
      {children}
    </AgentContext.Provider>
  );
};

export default AgentContext;